import { Icon } from '@cloudscape-design/components';

import type { ScorecardOpportunity } from '../common/scorecardInsights';
import type { StatisticStatus } from '../common/types';

type ScorecardOpportunitiesProps = {
  opportunities: ScorecardOpportunity[];
};

const STATUS_META: Record<StatisticStatus, { label: string; classes: string }> = {
  'above-average': { label: 'Above Average', classes: 'bg-emerald-50 text-emerald-700' },
  average: { label: 'Average', classes: 'bg-blue-50 text-blue-700' },
  'below-average': { label: 'Below Average', classes: 'bg-amber-100 text-amber-900' },
  'extremely-low': { label: 'Extremely Low', classes: 'bg-red-50 text-red-700' },
};

const OPPORTUNITY_ICONS: Record<
  ScorecardOpportunity['id'],
  'contact' | 'star' | 'undo' | 'calendar'
> = {
  reviews: 'contact',
  rating: 'star',
  'response-rate': 'undo',
  'response-time': 'calendar',
};

export function ScorecardOpportunities({ opportunities }: ScorecardOpportunitiesProps) {
  if (opportunities.length === 0) {
    return null;
  }

  return (
    <section
      aria-labelledby="scorecard-opportunities-title"
      className="overflow-hidden rounded-xl border-2 border-slate-900 bg-white"
    >
      <div className="border-b border-slate-200 px-4 py-3">
        <h3 id="scorecard-opportunities-title" className="text-lg font-bold text-slate-950">
          Your Biggest Opportunities
        </h3>
        <p className="mt-0.5 text-sm leading-5 text-slate-600">
          Combined performance across all review sites, last 180 days.
        </p>
      </div>
      <ol className="divide-y divide-slate-200 px-4">
        {opportunities.map((opportunity) => {
          const status = STATUS_META[opportunity.status];
          return (
            <li
              key={opportunity.id}
              className="grid grid-cols-[2rem_minmax(0,1fr)] items-start gap-3 py-2.5"
            >
              <span
                className={`flex h-8 w-8 items-center justify-center rounded-full ${status.classes}`}
              >
                <Icon name={OPPORTUNITY_ICONS[opportunity.id]} />
              </span>
              <div className="min-w-0">
                <p className="font-bold leading-5 text-slate-950">{opportunity.value}</p>
                <p className="text-sm leading-5 text-slate-600">{opportunity.action}</p>
                <span className="sr-only">
                  {opportunity.title}: {status.label}
                </span>
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
}
